<template>
  <material-card offset="75" class="ma-2 pa-3">
    <v-card-text style="position: relative">
      <div v-if="login">
        <p class="subheading">{{ loginTitle }}</p>

        <p class="title">{{ login }}</p>
      </div>

      <div v-if="partner && partner.name">
        <p class="subheading">Nazwa Partnera</p>

        <p class="title">{{ partner.name }}</p>
      </div>

      <div v-if="profile.firstName || profile.lastName">
        <p class="subheading">Imię i nazwisko</p>

        <p class="title">{{ profile.firstName }} {{ profile.lastName }}</p>
      </div>

      <div v-if="profile.city">
        <p class="subheading">Adres</p>

        <p class="title">{{ profile | address }}</p>
      </div>

      <div v-if="profile.email && profile.email !== login">
        <p class="subheading">E-mail (kontaktowy)</p>

        <p class="title">{{ profile.email }}</p>
      </div>

      <div v-if="profile.phoneNumber">
        <p class="subheading">Numer telefonu</p>

        <p class="title">{{ profile.phoneNumber }}</p>
      </div>

      <div v-if="profile.pesel">
        <p class="subheading">Pesel</p>

        <p class="title">{{ profile.pesel }}</p>
      </div>

      <div v-if="profile.gender">
        <p class="subheading">Płeć</p>

        <p class="title">{{ profile.gender | gender }}</p>
      </div>

      <div v-if="profile.birthDate">
        <p class="subheading">Data urodzenia</p>

        <p class="title">{{ profile.birthDate }}</p>
      </div>

      <div v-if="profile.userId && (isCurrentUser || canGetAccountMfa)">
        <p class="subheading">Uwierzytelnianie dwuskładnikowe</p>

        <p class="title">{{ mfa.enabled ? 'Włączone' : 'Wyłączone' }}</p>
      </div>
    </v-card-text>

    <v-layout slot="actions" justify-center row wrap>
      <check-permission join-type="OR" entity-type="profile" :permissions="updatePermissions" :entity-id="profile.id">
        <v-btn dark flat color="blue darken-2" @click="showEditModal"> <v-icon left> edit </v-icon> Edytuj </v-btn>
      </check-permission>

      <check-permission entity-type="profile" permissions="USER.DELETE" :entity-id="profile.id">
        <v-btn dark flat color="red darken-2" @click="showDeleteDialog">
          <v-icon left>mdi-trash-can-outline</v-icon>Usuń konto
        </v-btn>
      </check-permission>

      <check-permission
        v-if="profile.email"
        permissions="ACCOUNT.PASSWORD.RESET"
        entity-type="profile"
        :entity-id="profile.id"
      >
        <v-btn dark flat color="blue darken-2" @click="showPasswordDialog">
          <v-icon left>lock</v-icon>Resetuj hasło
        </v-btn>
      </check-permission>

      <check-permission
        v-if="userActive"
        permissions="ACCOUNT.PASSWORD.CHANGE"
        entity-type="profile"
        :entity-id="profile.id"
      >
        <v-btn dark flat color="blue darken-2" @click="openModal('changePassword')">
          <v-icon left>lock</v-icon>Zmień hasło
        </v-btn>
      </check-permission>

      <check-permission
        v-if="userActive && isCurrentUser"
        permissions="ACCOUNT.MFA.MANAGE"
        entity-type="profile"
        :entity-id="profile.id"
      >
        <v-btn v-if="mfa.enabled" dark flat color="red darken-2" @click="openDisableMfaDialog">
          <v-icon left>mdi-shield-off-outline</v-icon>Wyłącz MFA
        </v-btn>

        <v-btn v-else dark flat color="blue darken-2" @click="openMfaDialog">
          <v-icon left>mdi-shield-key-outline</v-icon>Włącz MFA
        </v-btn>
      </check-permission>

      <check-permission
        v-if="userToConfirmManually"
        permissions="ACCOUNT.CONFIRM_MANUALLY"
        entity-type="profile"
        :entity-id="profile.id"
      >
        <v-btn dark flat color="blue darken-2" @click="openModal('confirmUser')">
          <v-icon left>check</v-icon>Potwierdź konto
        </v-btn>
      </check-permission>

      <check-permission
        v-if="roleLinkField"
        join-type="OR"
        entity-type="profile"
        :entity-id="profile.id"
        :permissions="['USER.ROLE_MANAGE', 'PERMISSION_RELATION.ADD', 'PERMISSION_RELATION.DELETE']"
      >
        <router-link :to="{ name: roleLinkField, params: { [`${roleLinkParam}`]: profile.userId } }">
          <v-btn dark flat color="blue darken-2"> <v-icon left>mdi-sitemap</v-icon>role </v-btn>
        </router-link>
      </check-permission>
    </v-layout>

    <material-dialog
      :id="profile.id"
      cancel-text="Anuluj"
      confirm-text="Usuń"
      title="Potwierdź usunięcie"
      :text="passwordText"
      :show="profiledeleteDialog"
      @close="hideDeleteDialog"
      @confirm="deleteProfile"
    />

    <material-dialog
      :id="profile.id"
      cancel-text="Anuluj"
      confirm-text="Wyślij"
      title="Reset hasła"
      :text="passwordText"
      :width="450"
      :show="passwordDialog"
      @close="hidePasswordDialog"
      @confirm="sendConfirmation"
    />

    <change-password-modal
      :show="modals.changePassword"
      @close="closeModal('changePassword')"
      @change-password="changePassword($event)"
    />

    <confirm-user-modal
      :show="modals.confirmUser"
      @close="closeModal('confirmUser')"
      @confirm-user="confirmUser($event)"
    />

    <mfa-dialog
      v-model="mfa.dialog"
      :mode="mfa.mode"
      :step="mfa.step"
      :password="mfa.password"
      :code="mfa.code"
      :message="mfa.message"
      :message-type="mfa.messageType"
      :loading="mfa.loading"
      :resend-seconds="mfa.resendSeconds"
      @update-password="mfa.password = $event"
      @update-code="mfa.code = $event"
      @close="closeMfaDialog"
      @submit="submitMfaDialog"
      @resend="mfa.mode === 'enable' ? requestEnableMfa() : requestDisableMfa()"
    />
  </material-card>
</template>

<script>
import {
  address,
  formatError,
  GenderTranslation,
  isOfficial,
  SessionStorage,
  UserState
} from '@witkac/kartamiejska-shared';

import ChangePasswordModal from '@/components/helpers/ChangePasswordModal';
import ConfirmUserModal from '@/components/helpers/ConfirmUserModal';
import MfaDialog from '@/components/helpers/MfaDialog';
import MaterialCard from '@/components/material/Card';
import MaterialDialog from '@/components/material/Dialog';
import { deleteProfileMixin } from '@/mixins/deleteProfile';
import { displayModalMixin } from '@/mixins/displayModal';
import { notificationsMixin } from '@/mixins/notifications';
import { resetPasswordMixin } from '@/mixins/resetPassword';
import {
  buildMfaCooldownMessage,
  clearStoredMfaCooldown,
  getMfaCooldownSeconds,
  getMfaResendStorageKey,
  getStoredMfaCooldownSeconds,
  MFA_PROFILE_RESEND_STORAGE_KEY,
  storeMfaCooldown
} from '@/utils/mfa';

// @vuese
// Wyświetlanie tabeli z danymi klienta
// @group Client-Profile
export default {
  components: { ChangePasswordModal, MaterialCard, MaterialDialog, ConfirmUserModal, MfaDialog },
  filters: {
    gender(val) {
      return GenderTranslation[val];
    },
    address
  },
  mixins: [deleteProfileMixin, displayModalMixin, notificationsMixin, resetPasswordMixin],
  props: {
    // Tytuł do loginu
    loginTitle: {
      type: String,
      required: false,
      default: 'Login'
    },
    // Login użytkownika
    login: {
      type: String,
      required: false
    },
    // Dane profilu
    profile: {
      type: Object,
      required: true
    },
    // Dane partnera
    partner: {
      type: Object,
      required: false
    },
    // Link do roli
    roleLinkField: {
      type: String,
      required: false,
      default: undefined
    },
    // Param roli
    roleLinkParam: {
      type: String,
      required: false,
      default: 'id'
    },
    // Uprawnienia do edycji
    updatePermissions: {
      type: [Array, String],
      required: true
    }
  },
  data() {
    return {
      UserState,
      canGetAccountMfa: false,
      mfa: {
        enabled: false,
        dialog: false,
        mode: 'enable',
        step: 'password',
        password: '',
        code: '',
        message: '',
        messageType: 'info',
        loading: false,
        resendSeconds: 0,
        resendTimerId: null
      }
    };
  },
  computed: {
    isOfficial,
    userId() {
      return this.profile.userId;
    },
    userActive() {
      return this.profile.user.state === UserState.ACTIVE;
    },
    userToConfirmManually() {
      return !this.profile.email && this.profile.user.state === UserState.NOT_CONFIRMED;
    },
    isCurrentUser() {
      const currentUserId = localStorage.getItem(SessionStorage.USER_ID);
      return !!currentUserId && `${currentUserId}` === `${this.userId}`;
    }
  },
  watch: {
    userId: {
      immediate: true,
      handler() {
        this.canGetAccountMfa = false;
        this.mfa.enabled = false;
        this.loadMfaStatus();
      }
    }
  },
  beforeDestroy() {
    this.clearMfaResendTimer();
  },
  methods: {
    // @vuese
    // Funkcja wywołuje modal do edycji danych klienta
    showEditModal() {
      this.$emit('showEditModal');
    },

    // @vuese
    // Zmiana hasła
    changePassword(data) {
      this.$store
        .dispatch('auth/changePassword', { ...data, userId: this.userId })
        .then(() => {
          this.notifySuccess('Zmieniono hasło użytkownika.');
          this.closeModal('changePassword');
        })
        .catch(error => this.notifyError(formatError(error)));
    },
    // @vuese
    // Potwierdzenie użytkownika
    confirmUser(data) {
      this.$store
        .dispatch('auth/confirmManually', { password: data.password, userId: this.userId })
        .then(() => this.$router.go())
        .catch(error => this.notifyError(formatError(error)));
    },
    async loadMfaStatus() {
      const userId = this.userId;
      const isCurrentUser = this.isCurrentUser;
      if (!isCurrentUser) {
        try {
          const [canGetAccountMfa] = await this.$store.dispatch('access/check', ['ACCOUNT.MFA.GET']);
          if (userId !== this.userId) {
            return;
          }
          this.canGetAccountMfa = !!canGetAccountMfa;
        } catch {
          this.canGetAccountMfa = false;
        }

        if (!this.canGetAccountMfa) {
          this.mfa.enabled = false;
          return;
        }
      }

      const action = this.isCurrentUser ? 'auth/getOwnMfaStatus' : 'auth/getAccountMfaStatus';
      this.$store
        .dispatch(action, userId)
        .then(enabled => {
          if (userId === this.userId) {
            this.mfa.enabled = !!enabled;
          }
        })
        .catch(() => {
          if (userId === this.userId) {
            this.mfa.enabled = false;
          }
        });
    },
    openMfaDialog() {
      this.mfa.dialog = true;
      this.mfa.mode = 'enable';
      const cooldown = this.getStoredMfaResendCooldown('enable');
      this.mfa.step = cooldown > 0 ? 'code' : 'password';
      this.mfa.password = '';
      this.mfa.code = '';
      if (cooldown > 0) {
        this.startMfaResendCountdown(cooldown, 'enable');
        this.mfa.message = buildMfaCooldownMessage();
      } else {
        this.mfa.message = '';
      }
      this.mfa.messageType = 'info';
    },
    openDisableMfaDialog() {
      this.mfa.dialog = true;
      this.mfa.mode = 'disable';
      this.mfa.step = 'code';
      this.mfa.password = '';
      this.mfa.code = '';
      const cooldown = this.getStoredMfaResendCooldown('disable');
      if (cooldown > 0) {
        this.startMfaResendCountdown(cooldown);
        this.mfa.message = buildMfaCooldownMessage();
        this.mfa.messageType = 'info';
        return;
      }

      this.mfa.message = '';
      this.mfa.messageType = 'info';
      this.requestDisableMfa(true);
    },
    closeMfaDialog() {
      this.mfa.dialog = false;
      this.mfa.loading = false;
      this.clearMfaResendTimer();
      this.mfa.mode = 'enable';
      this.mfa.step = 'password';
      this.mfa.password = '';
      this.mfa.code = '';
      this.mfa.message = '';
      this.mfa.messageType = 'info';
    },
    setMfaError(error) {
      const message = formatError(error);
      this.mfa.message = message;
      this.mfa.messageType = 'error';
    },
    clearMfaResendTimer() {
      if (this.mfa.resendTimerId) {
        clearInterval(this.mfa.resendTimerId);
        this.mfa.resendTimerId = null;
      }
    },
    startMfaResendCountdown(seconds) {
      this.clearMfaResendTimer();
      this.mfa.resendSeconds = Math.max(0, seconds);

      if (this.mfa.resendSeconds <= 0) {
        return;
      }

      this.mfa.resendTimerId = setInterval(() => {
        if (this.mfa.resendSeconds <= 1) {
          this.clearMfaResendTimer();
          this.mfa.resendSeconds = 0;
          this.clearStoredMfaResendCooldown();
          return;
        }
        this.mfa.resendSeconds -= 1;
      }, 1000);
    },
    applyMfaRequestResponse(response, showMessage = true) {
      this.mfa.step = 'code';
      this.storeMfaResendCooldown(response?.resendAvailableAt);
      const seconds = getMfaCooldownSeconds(response?.resendAvailableAt);
      this.startMfaResendCountdown(seconds);

      if (showMessage) {
        this.mfa.messageType = 'success';
        this.mfa.message = buildMfaCooldownMessage();
        return;
      }

      this.mfa.message = '';
      this.mfa.messageType = 'info';
    },
    getMfaResendStorageKey(mode = this.mfa.mode) {
      return getMfaResendStorageKey(MFA_PROFILE_RESEND_STORAGE_KEY, `${this.userId}.${mode}`);
    },
    getStoredMfaResendCooldown(mode = this.mfa.mode) {
      return getStoredMfaCooldownSeconds(MFA_PROFILE_RESEND_STORAGE_KEY, `${this.userId}.${mode}`);
    },
    storeMfaResendCooldown(resendAvailableAt, mode = this.mfa.mode) {
      storeMfaCooldown(MFA_PROFILE_RESEND_STORAGE_KEY, `${this.userId}.${mode}`, resendAvailableAt);
    },
    clearStoredMfaResendCooldown(mode = this.mfa.mode) {
      clearStoredMfaCooldown(MFA_PROFILE_RESEND_STORAGE_KEY, `${this.userId}.${mode}`);
    },
    submitMfaDialog() {
      if (this.mfa.mode === 'disable') {
        this.disableMfa();
        return;
      }

      if (this.mfa.step === 'password') {
        this.requestEnableMfa();
        return;
      }
      this.verifyEnableMfa();
    },
    requestEnableMfa() {
      if (!this.mfa.password) {
        this.setMfaError('Wprowadź aktualne hasło.');
        return;
      }

      this.mfa.loading = true;
      this.mfa.message = '';
      this.$store
        .dispatch('auth/requestEnableMfa', { userId: this.userId, password: this.mfa.password })
        .then(response => {
          this.applyMfaRequestResponse(response);
        })
        .catch(error => {
          this.setMfaError(error);
        })
        .finally(() => {
          this.mfa.loading = false;
        });
    },
    verifyEnableMfa() {
      if (!this.mfa.code) {
        this.setMfaError('Wprowadź kod MFA.');
        return;
      }

      this.mfa.loading = true;
      this.mfa.message = '';
      this.$store
        .dispatch('auth/enableMfa', { userId: this.userId, code: this.mfa.code })
        .then(() => {
          this.mfa.enabled = true;
          this.clearStoredMfaResendCooldown('enable');
          this.notifySuccess('Włączono MFA. Kolejne logowanie będzie wymagało kodu.');
          this.closeMfaDialog();
        })
        .catch(error => {
          this.setMfaError(error);
        })
        .finally(() => {
          this.mfa.loading = false;
        });
    },
    requestDisableMfa(silentOnSuccess = false) {
      this.mfa.loading = true;
      this.mfa.message = '';
      this.$store
        .dispatch('auth/requestDisableMfa', this.userId)
        .then(response => {
          this.applyMfaRequestResponse(response, !silentOnSuccess);
        })
        .catch(error => {
          const cooldown = this.getStoredMfaResendCooldown('disable');
          if (cooldown > 0) {
            this.mfa.step = 'code';
            this.startMfaResendCountdown(cooldown);
            this.mfa.message = buildMfaCooldownMessage();
            this.mfa.messageType = 'info';
            return;
          }

          this.setMfaError(error);
        })
        .finally(() => {
          this.mfa.loading = false;
        });
    },
    disableMfa() {
      if (!this.mfa.code) {
        this.setMfaError('Wprowadź kod MFA.');
        return;
      }

      this.mfa.loading = true;
      this.mfa.message = '';
      this.$store
        .dispatch('auth/disableMfa', { userId: this.userId, code: this.mfa.code })
        .then(() => {
          this.mfa.enabled = false;
          this.clearStoredMfaResendCooldown('disable');
          this.notifySuccess('Wyłączono MFA. Kolejne logowanie nie będzie wymagało kodu.');
          this.closeMfaDialog();
        })
        .catch(error => {
          this.setMfaError(error);
        })
        .finally(() => {
          this.mfa.loading = false;
        });
    }
  }
};
</script>
